class ICoreProcessSteps {
    root = null;
    visual = null;
    spacer = null;
    track = null;
    steps = [];
    panelIcon = null;
    panelNum = null;
    panelTitle = null;
    panelDesc = null;
    progressItems = [];

    stickyEnabled = false;
    mediaQuery = null;
    stickyMetrics = {
        width: 0,
        left: 0,
    };

    eventHandler = {};

    constructor(root) {
        this.root = root;

        if (!this.root) {
            return;
        }

        this.steps = [...this.root.querySelectorAll('.icore-process-step')];
        this.visual = this.root.querySelector('.icore-process-steps__visual');
        this.track = this.root.querySelector('.icore-process-steps__track');

        if (!this.steps.length || !this.visual || !this.track) {
            return;
        }

        this.panelIcon = this.root.querySelector('.icore-process-steps__icon i');
        this.panelNum = this.root.querySelector('.icore-process-steps__num');
        this.panelTitle = this.root.querySelector('.icore-process-steps__panel-title');
        this.panelDesc = this.root.querySelector('.icore-process-steps__panel-desc');
        this.progressItems = [...this.root.querySelectorAll('.icore-process-steps__prog i')];

        this.eventHandler = {
            click: this.onStepClick.bind(this),
            scroll: this.#onScrollResize.bind(this),
            resize: this.#onResize.bind(this),
            mediaChange: this.#onMediaChange.bind(this),
        };

        this.#initEvents();
        this.#initSticky();
    }

    #initEvents() {
        this.steps.forEach((step) => {
            step.addEventListener('click', this.eventHandler.click);
        });
    }

    #initSticky() {
        this.spacer = document.createElement('div');
        this.spacer.className = 'icore-process-steps__visual-spacer';
        this.spacer.setAttribute('aria-hidden', 'true');
        this.visual.after(this.spacer);

        this.mediaQuery = window.matchMedia('(min-width: 921px)');
        this.mediaQuery.addEventListener('change', this.eventHandler.mediaChange);

        if (this.mediaQuery.matches) {
            this.#enableJsSticky();
        }
    }

    #onMediaChange(event) {
        if (event.matches) {
            this.#enableJsSticky();
        } else {
            this.#disableJsSticky();
        }
    }

    #enableJsSticky() {
        if (this.stickyEnabled) {
            return;
        }

        this.stickyEnabled = true;
        this.root.classList.add('is--js-sticky');

        window.addEventListener('scroll', this.eventHandler.scroll, { passive: true });
        window.addEventListener('resize', this.eventHandler.resize, { passive: true });

        if (window.$iCore?.lenis) {
            window.$iCore.lenis.on('scroll', this.eventHandler.scroll);
        }

        requestAnimationFrame(() => {
            this.#updateStickyMetrics();
            this.#onScrollResize();
        });
    }

    #disableJsSticky() {
        if (!this.stickyEnabled) {
            return;
        }

        this.stickyEnabled = false;
        this.root.classList.remove('is--js-sticky');
        this.#resetVisualPosition();

        window.removeEventListener('scroll', this.eventHandler.scroll);
        window.removeEventListener('resize', this.eventHandler.resize);

        if (window.$iCore?.lenis?.off) {
            window.$iCore.lenis.off('scroll', this.eventHandler.scroll);
        }
    }

    #getStickyTop() {
        const raw = getComputedStyle(this.root)
            .getPropertyValue('--icore-process-steps-sticky-top')
            .trim();
        const parsed = parseFloat(raw);

        return Number.isNaN(parsed) ? 120 : parsed;
    }

    #getRootMetrics() {
        const rootRect = this.root.getBoundingClientRect();
        const gapRaw = getComputedStyle(this.root)
            .getPropertyValue('--icore-process-steps-gap')
            .trim();
        const gap = parseFloat(gapRaw);
        const columnGap = Number.isNaN(gap) ? 70 : gap;
        const columnWidth = (rootRect.width - columnGap) / 2;

        return {
            top: rootRect.top,
            bottom: rootRect.bottom,
            left: rootRect.left,
            columnWidth: columnWidth > 0 ? columnWidth : rootRect.width / 2,
        };
    }

    #updateStickyMetrics() {
        const wasPositioned = this.visual.classList.contains('is--fixed')
            || this.visual.classList.contains('is--bottom');

        if (wasPositioned) {
            this.visual.classList.remove('is--fixed', 'is--bottom');
            this.visual.style.width = '';
            this.visual.style.left = '';
        }

        const rootMetrics = this.#getRootMetrics();
        this.stickyMetrics = {
            width: rootMetrics.columnWidth,
            left: rootMetrics.left,
        };

        if (wasPositioned) {
            this.#onScrollResize();
        }
    }

    #onResize() {
        if (!this.stickyEnabled) {
            return;
        }

        this.#updateStickyMetrics();
        this.#onScrollResize();
    }

    #resetVisualPosition() {
        this.visual.classList.remove('is--fixed', 'is--bottom');
        this.visual.style.width = '';
        this.visual.style.left = '';
        this.spacer.style.display = 'none';
        this.spacer.style.height = '';
    }

    #onScrollResize() {
        if (!this.stickyEnabled) {
            return;
        }

        const stickyTop = this.#getStickyTop();
        const rootMetrics = this.#getRootMetrics();
        const visualHeight = this.visual.offsetHeight;

        this.stickyMetrics.width = rootMetrics.columnWidth;
        this.stickyMetrics.left = rootMetrics.left;

        // Section not yet scrolled into the sticky zone.
        if (rootMetrics.top > stickyTop) {
            this.#resetVisualPosition();
            return;
        }

        // Section end reached — pin card to bottom of container.
        if (rootMetrics.bottom <= stickyTop + visualHeight) {
            this.visual.classList.remove('is--fixed');
            this.visual.classList.add('is--bottom');
            this.visual.style.width = '';
            this.visual.style.left = '';
            this.spacer.style.display = 'block';
            this.spacer.style.height = `${visualHeight}px`;
            return;
        }

        this.visual.classList.remove('is--bottom');
        this.visual.classList.add('is--fixed');
        this.visual.style.width = `${this.stickyMetrics.width}px`;
        this.visual.style.left = `${this.stickyMetrics.left}px`;
        this.spacer.style.display = 'block';
        this.spacer.style.height = `${visualHeight}px`;
    }

    onStepClick(event) {
        const step = event.currentTarget;
        const index = Number(step.dataset.index);

        if (Number.isNaN(index)) {
            return;
        }

        this.setStep(index);
    }

    setStep(index) {
        this.steps.forEach((step, i) => {
            const isActive = i === index;
            step.classList.toggle('is--active', isActive);
            step.setAttribute('aria-selected', isActive ? 'true' : 'false');
            step.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        const active = this.steps[index];

        if (!active) {
            return;
        }

        if (this.panelNum) {
            this.panelNum.textContent = active.dataset.stepNum || '';
        }

        if (this.panelTitle) {
            this.panelTitle.textContent = active.dataset.panelTitle || '';
        }

        if (this.panelDesc) {
            this.panelDesc.textContent = active.dataset.panelDesc || '';
        }

        if (this.panelIcon) {
            this.panelIcon.className = active.dataset.icon || '';
        }

        this.progressItems.forEach((item, i) => {
            item.classList.toggle('is--active', i <= index);
        });
    }

    destroy() {
        this.#disableJsSticky();

        if (this.mediaQuery) {
            this.mediaQuery.removeEventListener('change', this.eventHandler.mediaChange);
        }

        if (this.spacer?.parentNode) {
            this.spacer.remove();
        }

        this.steps.forEach((step) => {
            step.removeEventListener('click', this.eventHandler.click);
        });
    }
}

export default ICoreProcessSteps;
